import { Injectable, inject } from '@angular/core';
import { LoggerService, SessionToken, JwtService } from '@enliven/angular';
import { Observable, Subject, map, of, shareReplay, take, filter, bufferTime } from 'rxjs';
import { SessionMessageType } from '../enums/session-message-type.enum';
import { SessionMessage } from '../models/session-message.model';
import { DirectLoginService } from './direct-login.service';
import { E360SessionService } from './e360-session.service';
import { E360UserService } from './e360-user.service';

/**
 * Service to manage session sharing across browser tabs using BroadcastChannel
 * Enables single sign-on experience where signing in on one tab authenticates all tabs
 */
@Injectable({
  providedIn: 'root'
})
export class SessionSharingService {
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly e360UserService: E360UserService = inject(E360UserService);
  private readonly directLoginService: DirectLoginService = inject(DirectLoginService);
  private readonly jwtService: JwtService = inject(JwtService);

  private readonly CHANNEL_NAME = 'e360-session-channel';
  private readonly REQUEST_TIMEOUT_MS = 1500; // Wait 1 second for responses

  private broadcastChannel: BroadcastChannel | null = null;
  private readonly messageSubject = new Subject<SessionMessage>();

  /**
   * Initialize the BroadcastChannel for cross-tab communication
   */
  initializeChannel(): void {
    if (!this.isBroadcastChannelSupported()) {
      this.logger.warn('BroadcastChannel not supported in this browser');
      return;
    }

    // Avoid creating multiple channels in the same tab
    if (this.broadcastChannel) {
      this.logger.debug('Session sharing channel already initialized');
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);

      // Listen for messages from other tabs
      this.broadcastChannel.onmessage = (event: MessageEvent<SessionMessage>) => {
        this.handleIncomingMessage(event.data);
      };

      this.logger.info('Session sharing channel initialized');
    } catch (error) {
      this.logger.error('Failed to initialize BroadcastChannel', { error });
    }
  }

  /**
   * Check if BroadcastChannel API is supported
   */
  private isBroadcastChannelSupported(): boolean {
    return typeof BroadcastChannel !== 'undefined';
  }

  /**
   * Request session from other tabs
   * Returns an Observable that emits if another tab has an active session
   * Will NOT share session if multiple tabs have different users signed in
   */
  requestSessionFromOtherTabs(): Observable<SessionMessage | null> {
    if (!this.broadcastChannel) {
      this.logger.warn('Cannot request session - BroadcastChannel not initialized');
      return of(null);
    }

    this.logger.info('Requesting session from other tabs...');

    // Collect all session responses within the timeout period using bufferTime
    const sessionResponses$ = this.messageSubject.pipe(
      filter(msg => msg.type === SessionMessageType.SESSION_RESPONSE),
      bufferTime(this.REQUEST_TIMEOUT_MS), // Collect responses for 1 second
      take(1), // Take the first buffer (after 1 second)
      map(responses => {
        this.logger.info('Session request buffer completed', {
          responsesReceived: responses.length,
          timeout: this.REQUEST_TIMEOUT_MS
        });

        if (responses.length === 0) {
          this.logger.info('No session responses from other tabs');
          return null;
        }

        // Check if all responses are from the same user (using unique user ID from Cognito 'sub' claim)
        const uniqueUserSubs = new Set(responses.map(r => r.payload?.userSub).filter(Boolean));

        this.logger.info('Checking user uniqueness', {
          uniqueUsers: uniqueUserSubs.size,
          userSubs: Array.from(uniqueUserSubs)
        });

        if (uniqueUserSubs.size > 1) {
          this.logger.warn('Multiple different users detected in other tabs, refusing to share session', {
            userCount: uniqueUserSubs.size,
            users: Array.from(uniqueUserSubs)
          });
          return null;
        }

        // All tabs have the same user, return the first valid response
        const validResponse = responses.find(r => r.payload?.tokens && r.payload?.userOrgId);

        if (validResponse) {
          this.logger.info('Session found from other tabs (same user in all tabs)', {
            userSub: validResponse.payload?.userSub,
            userOrgId: validResponse.payload?.userOrgId,
            responseCount: responses.length
          });
        } else {
          this.logger.warn('No valid response found despite receiving responses', {
            responseCount: responses.length
          });
        }

        return validResponse || null;
      }),
      shareReplay(1)
    );

    // Send request to other tabs
    this.broadcastMessage({
      type: SessionMessageType.REQUEST_SESSION
    });

    return sessionResponses$;
  }

  /**
   * Check if any other tabs have active sessions with the same refresh token
   * Returns an Observable that emits true if other tabs are still using the same refresh token, false otherwise
   * Used before revoking refresh token to avoid breaking other active tabs
   */
  checkForActiveSessionsInOtherTabs(refreshToken: string): Observable<boolean> {
    if (!this.broadcastChannel) {
      this.logger.warn('Cannot check for active sessions - BroadcastChannel not initialized');
      return of(false);
    }

    if (!refreshToken) {
      this.logger.warn('No refresh token provided for active session check');
      return of(false);
    }

    this.logger.info('Checking for active sessions with matching refresh token in other tabs...');

    // Collect all active session responses within the timeout period
    const activeSessionResponses$ = this.messageSubject.pipe(
      filter(msg => msg.type === SessionMessageType.ACTIVE_SESSION_RESPONSE),
      bufferTime(this.REQUEST_TIMEOUT_MS), // Collect responses for 1 second
      take(1), // Take the first buffer (after 1 second)
      map(responses => {
        const hasActiveSessions = responses.length > 0;
        this.logger.info('Active session check completed', {
          activeTabsFound: responses.length,
          hasActiveSessions
        });
        return hasActiveSessions;
      }),
      shareReplay(1)
    );

    // Send check request to other tabs with the refresh token to compare
    this.broadcastMessage({
      type: SessionMessageType.CHECK_ACTIVE_SESSIONS,
      payload: {
        refreshToken,
        timestamp: Date.now()
      }
    });

    return activeSessionResponses$;
  }

  /**
   * Setup local session from tokens received from another tab
   */
  setupSessionFromSharedTokens(tokens: SessionToken, userOrgId: string, orgId: string): Observable<boolean> {
    this.logger.info('Setting up session from shared tokens', { userOrgId, orgId });

    // Set tokens in session service
    this.e360SessionService.setSessionTokens(tokens);
    this.e360SessionService.cleanupStorageAfterSignIn(false); // Direct Login style
    this.e360UserService.userOrgId = userOrgId;
    this.e360SessionService.assignUserOrgIdIfNoAuthenticatingOrgId(userOrgId);

    // Retrieve org data and setup session (same as normal Direct Login flow)
    return this.directLoginService.retrieveOrgData(orgId, userOrgId, this.logger).pipe(
      map(() => {
        this.logger.info('Session setup complete from shared tokens');
        return true;
      })
    );
  }

  /**
   * Handle incoming messages from other tabs
   */
  private handleIncomingMessage(message: SessionMessage): void {
    this.logger.debug('Received message from other tab', { type: message.type });

    switch (message.type) {
      case SessionMessageType.REQUEST_SESSION:
        this.handleSessionRequest();
        break;

      case SessionMessageType.CHECK_ACTIVE_SESSIONS:
        this.handleActiveSessionCheck(message);
        break;

      case SessionMessageType.SESSION_RESPONSE:
      case SessionMessageType.ACTIVE_SESSION_RESPONSE:
        // Emit to subscribers
        this.messageSubject.next(message);
        break;
    }
  }

  /**
   * Handle session request from another tab
   * If this tab has an active session, respond with session data
   */
  private handleSessionRequest(): void {
    this.logger.info('Received session request from another tab');

    // Check if current tab has an active Direct Login session (not SSO)
    const hasActiveSession = this.e360SessionService.session?.sessiontokens?.idToken && !this.e360SessionService.isSSO;

    if (hasActiveSession) {
      this.logger.info('This tab has active session, responding with tokens');

      const tokens: SessionToken = {
        idToken: this.e360SessionService.idToken,
        accessToken: this.e360SessionService.accessToken,
        refreshToken: this.e360SessionService.refreshToken
      };

      // Decode idToken to extract the 'sub' (unique user ID from Cognito)
      let userSub: string | undefined;
      try {
        const decodedToken: any = this.jwtService.decodeToken(tokens.idToken);
        userSub = decodedToken?.sub;
      } catch (error) {
        this.logger.error('Failed to decode idToken for user sub', { error });
      }

      this.broadcastMessage({
        type: SessionMessageType.SESSION_RESPONSE,
        payload: {
          tokens,
          userOrgId: this.e360UserService.userOrgId,
          orgId: this.e360SessionService.orgId,
          userSub,
          timestamp: Date.now()
        }
      });

      this.logger.info('Session response sent to requesting tab', { userSub });
    } else {
      this.logger.info('This tab does not have an active Direct Login session to share');
    }
  }

  /**
   * Handle active session check from another tab
   * Only respond if this tab has an active session with the same refresh token
   */
  private handleActiveSessionCheck(message: SessionMessage): void {
    this.logger.info('Received active session check from another tab');

    const incomingRefreshToken = message.payload?.refreshToken;

    if (!incomingRefreshToken) {
      this.logger.warn('Active session check received without refresh token');
      return;
    }

    // Check if current tab has an active Direct Login session (not SSO)
    const hasActiveSession = this.e360SessionService.session?.sessiontokens?.idToken && !this.e360SessionService.isSSO;

    if (hasActiveSession) {
      const currentRefreshToken = this.e360SessionService.refreshToken;

      // Only respond if the refresh token matches
      if (currentRefreshToken === incomingRefreshToken) {
        this.logger.info('This tab has active session with matching refresh token, responding with confirmation');

        this.broadcastMessage({
          type: SessionMessageType.ACTIVE_SESSION_RESPONSE,
          payload: {
            timestamp: Date.now()
          }
        });
      } else {
        this.logger.info('This tab has active session but refresh token does not match, not responding');
      }
    } else {
      this.logger.info('This tab does not have an active session');
    }
  }

  /**
   * Broadcast a message to all other tabs
   */
  private broadcastMessage(message: SessionMessage): void {
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage(message);
      } catch (error) {
        this.logger.error('Failed to broadcast message', { message, error });
      }
    }
  }

  /**
   * Clean up the BroadcastChannel
   */
  destroy(): void {
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
      this.logger.info('Session sharing channel closed');
    }
    this.messageSubject.complete();
  }
}
